import React from "react";
import { useParams } from "react-router-dom";
import { gql } from "@apollo/client";
import {
  Box,
  Container,
  Stack,
  Paper,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import {
  Header,
  ConditionalRenderer,
  useLanguageTranslator,
  CoverImage,
  StatusTag,
} from "../../../components";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";

import imagePlaceholder from "../../../@lib/images/image-placeholder.svg";
import { useApolloQuery } from "../../../hooks";
import { baseURL } from "../../../utils/constants";
import { PDFDocIcon } from "../../../@lib/icons";

const policyQueryDocument = gql`
  query policyQuery($policyId: ID!) {
    policy(id: $policyId) {
      title
      description
      documentTitle
      country {
        name
      }
      submittedBy {
        firstName
        lastName
      }
      policyCategory {
        name
      }
      theme {
        title
      }
      geographicalArea {
        name
      }
      practices {
        title
      }
      people {
        title
        firstname
        lastname
      }
      rejectionReason
      picture {
        source
        originalFilename
      }
      document {
        source
        originalFilename
      }
      link
      date
      status
      statusLabel
    }
  }
`;

const PolicyDetails: React.FC = () => {
  const { translator } = useLanguageTranslator();
  const params = useParams();

  const { data, loading } = useApolloQuery({
    queryDocument: policyQueryDocument,
    variables: { policyId: params.policyId },
  });

  return (
    <div>
      <Header title={translator("Policies")} subtitle="" />

      <Container sx={{ my: 3 }}>
        <Stack direction={{ lg: "row" }} spacing={3}>
          <Paper component={Container} sx={{ py: 3, flex: 3 }}>
            <CoverImage
              image={
                data?.policy?.picture?.source
                  ? `${baseURL}${data?.policy?.picture?.source}`
                  : imagePlaceholder
              }
              height={400}
              mb={2}
            />

            <Box mt={3}>
              {loading ? (
                <>
                  <Skeleton
                    variant="text"
                    height={32}
                    width={200}
                    sx={{ mb: 0.5 }}
                  />
                  <Skeleton variant="text" height={16} width="80%" />
                  <Skeleton variant="text" height={16} width="40%" />
                </>
              ) : (
                <>
                  <StatusTag
                    label={data?.policy?.statusLabel}
                    status={data?.policy?.status}
                  />
                  <ConditionalRenderer condition={data?.policy?.rejectionReason}>
                    <Typography variant="h5" fontWeight={300} mt={1}>
                      {translator(`Rejection Reason`)}:{" "}
                      {data?.policy?.rejectionReason}
                    </Typography>
                  </ConditionalRenderer>
                  <Typography variant="h4" gutterBottom>
                    {data?.policy.title}
                  </Typography>
                  <Typography variant="body2">
                    {data?.policy.description}
                  </Typography>
                </>
              )}
            </Box>

            <Grid container spacing={1} my={2}>
              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("Practices")}
                </Typography>
                <ConditionalRenderer
                  condition={data?.policy?.practices?.length <= 0}
                >
                  {translator("None")}
                </ConditionalRenderer>
                <ConditionalRenderer
                  condition={data?.policy?.practices?.length > 0}
                >
                  <Stack height={"14dvh"} sx={{ overflowY: "auto" }}>
                    {data?.policy?.practices?.map((elem: { title: string }) => (
                      <Chip
                        title={elem?.title}
                        label={elem?.title?.slice(0, 30)}
                        sx={{
                          my: 1,
                          border: "1px solid #00AAB0",
                          backgroundColor: "#eff1f7",
                          color: "#00AAB0",
                        }}
                      />
                    ))}
                  </Stack>
                </ConditionalRenderer>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("People")}
                </Typography>
                <ConditionalRenderer
                  condition={data?.policy?.people?.length <= 0}
                >
                  {translator("None")}
                </ConditionalRenderer>
                <ConditionalRenderer
                  condition={data?.policy?.people?.length > 0}
                >
                  <Stack height={"14dvh"} sx={{ overflowY: "auto" }}>
                    {data?.policy?.people?.map(
                      (elem: { firstname: string; lastname: string }) => (
                        <Chip
                          title={`${elem?.firstname} ${elem?.lastname}`}
                          label={`${elem?.firstname} ${elem?.lastname}`}
                          sx={{
                            my: 1,
                            border: "1px solid #00AAB0",
                            backgroundColor: "#eff1f7",
                            color: "#00AAB0",
                          }}
                        />
                      )
                    )}
                  </Stack>
                </ConditionalRenderer>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("Country")}
                </Typography>
                <Typography mb={2}>{data?.policy.country.name}</Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  {translator("generic.Date Voted")}
                </Typography>
                <Typography mb={2}>{data?.policy.date || "--"}</Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  {translator("generic.Geographical Zone")}
                </Typography>
                <Typography mb={2}>
                  {data?.policy.geographicalArea.name}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("Theme")}
                </Typography>
                <Typography mb={2}>{data?.policy.theme.title}</Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  {translator("dashboardTableHead.Category")}
                </Typography>
                <Typography mb={2}>
                  {data?.policy.policyCategory.name}
                </Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  {translator("External Policy Link")}
                </Typography>
                <Typography mb={2}>
                  {data?.policy.link ? (
                    <Link
                      href={data.policy.link}
                      title={data.policy.link}
                      target="_BLANK"
                      display="block"
                    >
                      {data.policy.link?.slice(0, 22)}...
                    </Link>
                  ) : (
                    "--"
                  )}
                </Typography>
              </Grid>
            </Grid>
            <ConditionalRenderer condition={!data?.policy.document?.source}>
              <Typography variant="h3" textAlign={"center"}>
                {translator("No Document")}
              </Typography>
            </ConditionalRenderer>
            <ConditionalRenderer condition={data?.policy?.document?.source}>
              <Stack
                sx={{
                  my: 2,
                  p: 1,
                  border: "1px solid #F1F1F1",
                  borderRadius: "10px",
                }}
                direction={{ lg: "row" }}
                alignItems={{ lg: "center" }}
                justifyContent={{ lg: "space-between" }}
              >
                <div style={{ display: "flex" }}>
                  <Stack
                    sx={{
                      backgroundColor: "#fff2f2",
                      width: "90px",
                      height: "82px",
                      position: "relative",
                      zIndex: "0",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ position: "absolute", zIndex: "1000" }}>
                      <PDFDocIcon />
                    </div>
                  </Stack>
                  <Stack direction={{ lg: "column" }} sx={{ mx: 1 }}>
                    <Typography
                      variant="h4"
                      fontWeight={600}
                      color={"#494E54"}
                      fontSize={"15px"}
                      mt={2}
                      gutterBottom
                      title={data?.policy?.document?.originalFilename}
                    >
                      {data?.policy?.document?.originalFilename?.length > 40
                        ? `${data?.policy?.document?.originalFilename?.slice(
                            0,
                            40
                          )}...`
                        : data?.policy?.document?.originalFilename}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={600}
                      color={"#494E54"}
                      fontSize={"15px"}
                      gutterBottom
                      title={data?.policy?.documentTitle}
                    >
                      {data?.policy?.documentTitle?.length > 40
                        ? `${data?.policy?.documentTitle?.slice(0, 40)}...`
                        : data?.policy?.documentTitle}
                    </Typography>
                  </Stack>
                </div>
                <Stack
                  direction={{ lg: "column" }}
                  justifyContent={{ lg: "space-between" }}
                >
                  <Link
                    href={`${baseURL}${data?.policy.document?.source}`}
                    target="_blank"
                  >
                    <Button
                      sx={{
                        px: 5,
                        backgroundColor: "#F6A000",
                        color: "#ffffff",
                        "&:hover": {
                          backgroundColor: "primary.light",
                        },
                        width: "100%",
                      }}
                    >
                      {translator("dashboardTableHead.View")}
                    </Button>
                  </Link>
                  <Button
                    sx={{
                      px: 6,
                      mt: 1,
                      backgroundColor: "#00AAB0",
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: "primary.light",
                      },
                    }}
                    onClick={() => {
                      const fileUrl = `${baseURL}${data?.policy.document?.source}`;
                      fetch(fileUrl)
                        .then((response) => response.blob())
                        .then((blob) => {
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = url;
                          link.download =
                            data?.policy.document?.originalFilename ||
                            "Document.pdf";
                          link.click();
                          window.URL.revokeObjectURL(url);
                        })
                        .catch((err) =>
                          console.error("Error downloading the file", err)
                        );
                    }}
                  >
                    {translator("Download")}
                  </Button>
                </Stack>
              </Stack>
            </ConditionalRenderer>
          </Paper>

          <Paper component={Container} sx={{ py: 3, flex: 1 }}>
            <Typography variant="h4" gutterBottom>
              {translator("Related Policies")}
            </Typography>
          </Paper>
        </Stack>
      </Container>
    </div>
  );
};

export { PolicyDetails };
